import React, { useState, useEffect } from "react";
import { FaTree, FaTh, FaCube, FaGlobe } from "react-icons/fa";
import styled from "styled-components";
import { motion } from "framer-motion";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "../../authentication/firebase";
import { CircularProgress } from "@mui/material";

// Icon mapping for dynamic rendering
const iconMap = {
  FaTree: <FaTree />,
  FaTh: <FaTh />,
  FaCube: <FaCube />,
  FaGlobe: <FaGlobe />,
};

const Footprint = () => {
  const [footprintData, setFootprintData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Function to fetch footprint data from Firebase
  const fetchFootprintFromFirebase = async () => {
    try {
      setLoading(true);
      
      const footprintCollectionRef = collection(db, "footprint");
      const footprintQuery = query(footprintCollectionRef, orderBy("order", "asc"));
      const footprintSnapshot = await getDocs(footprintQuery);
      
      const fetchedFootprint = footprintSnapshot.docs.map(doc => ({
        firebaseId: doc.id,
        ...doc.data()
      }));
      
      setFootprintData(fetchedFootprint);
      
    } catch (error) {
      console.error("Error fetching footprint data from Firebase:", error);
      setError("Failed to load environmental data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFootprintFromFirebase();
  }, []);

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress color="inherit" size={50} />
        <LoadingText>Loading environmental data...</LoadingText>
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        <p>{error}</p>
        <button onClick={fetchFootprintFromFirebase} className="primary-btn">
          Try Again
        </button>
      </ErrorContainer>
    );
  }

  return (
    <Styled>
      <motion.h2 
        className="title"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        Wroot Impact, Reimagined
      </motion.h2>
      <motion.p 
        className="subtitle"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        Real-world outcomes driven by our dedication to a healthier, more balanced planet.
      </motion.p>
      
      {footprintData.length === 0 ? (
        <NoDataMessage>
          No environmental data available at the moment. Check back later!Hello
        </NoDataMessage>
      ) : (
        <div className="grid">
          {footprintData.map((item, index) => (
            <motion.div 
              className="card" 
              key={item.firebaseId || index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="icon">
                {iconMap[item.iconName]}
              </div>
              <h3 className="value">{item.value}</h3>
              <div className="label">{item.label}</div>
            </motion.div>
          ))}
        </div>
      )}
    </Styled>
  );
};

export default Footprint;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  gap: 20px;
  text-align: center;
  width: var(--section-width);
  margin: 0 auto;
`;

const LoadingText = styled.p`
  color: var(--text-color);
  font-size: 1.1rem;
  margin: 0;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  gap: 20px;
  text-align: center;
  width: var(--section-width);
  margin: 0 auto;
  
  p {
    color: var(--text-color);
    font-size: 1.1rem;
    margin: 0;
  }
  
  button {
    padding: 12px 24px;
    border-radius: 50px;
    border: none;
    background: var(--primary-color);
    color: var(--text-color);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      opacity: 0.9;
    }
  }
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: var(--text-gray-color);
  font-size: 1.1rem;
  background: var(--section-bg-color);
  border-radius: 12px;
  margin: 20px 0;
`;

const Styled = styled.div`
  text-align: center;
  width: var(--section-width);
  padding: var(--section-margin) 0;
  margin: 0 auto;
  .title {
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  .subtitle {
    font-size: 1rem;
    color: #666;
    max-width: 600px;
    margin: 0 auto 3rem auto;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 2rem;
    justify-content: space-between;
    align-items: center;
    justify-items: center;
  }

  .card {
    text-align: center;
    padding: 1rem;
    max-width: 200px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .icon {
    font-size: 2rem;
    color: #4caf50;
    background: #f1f9f2;
    width: 3.5rem;
    height: 3.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
    margin-bottom: 1rem;
  }

  .value {
    color: #388e3c;
    margin: 0;
  }

  .label {
    margin-top: 0.5rem;
    color: #777;
    font-size: 0.95rem;
  }
`;
